<template>
    <div>
        <FormLabel 
            v-if="!props.hideLabel"
            :for="props.id" 
            :required="props.required"
        >{{ props.label }}</FormLabel>

        <div class="tw-relative">
            <!-- Main selector button -->
            <button
                type="button"
                :id="props.id"
                @click="toggleDropdown"
                :class="[
                    'tw-w-full tw-h-[40px] tw-outline-none tw-text-sm tw-rounded-md tw-border tw-border-solid tw-px-4 tw-duration-200 tw-flex tw-items-center tw-justify-between tw-bg-white',
                    'focus:tw-border-primary-500 hover:tw-border-gray-300',
                    !!props.error && '!tw-border-red-500',
                    isOpen && 'tw-border-primary-500',
                    $root.$i18n.locale == 'ar' && 'tw-text-right'
                ]"
            >
                <div class="tw-flex tw-items-center tw-gap-3 tw-flex-1 tw-min-w-0">
                    <icon 
                        v-if="props.leftIcon"
                        :icon="props.leftIcon" 
                        :class="[
                            'tw-text-xl tw-text-gray-500 tw-duration-200 tw-flex-shrink-0',
                            !!props.error && '!tw-text-red-500',
                            isOpen && 'tw-text-primary-500'
                        ]"
                    />
                    <span :class="[
                        selectedItems.length > 0 ? 'tw-text-gray-900' : 'tw-text-gray-500',
                        'tw-font-medium tw-truncate'
                    ]">
                        {{ selectedItems.length > 0 
                            ? `${selectedItems.length} ${selectedItems.length === 1 ? 'item' : 'items'} selected`
                            : props.placeholder 
                        }}
                    </span>
                </div>
                <icon 
                    :icon="isOpen ? 'solar:alt-arrow-up-outline' : 'solar:alt-arrow-down-outline'"
                    :class="[
                        'tw-text-xl tw-text-gray-500 tw-duration-200 tw-flex-shrink-0',
                        !!props.error && '!tw-text-red-500',
                        isOpen && 'tw-text-primary-500'
                    ]"
                />
            </button>

            <!-- Dropdown -->
            <transition
                enter-active-class="tw-transition-all tw-duration-200 tw-ease-out"
                enter-from-class="tw-opacity-0 tw-scale-95 tw-translate-y-2"
                enter-to-class="tw-opacity-100 tw-scale-100 tw-translate-y-0"
                leave-active-class="tw-transition-all tw-duration-150 tw-ease-in"
                leave-from-class="tw-opacity-100 tw-scale-100 tw-translate-y-0"
                leave-to-class="tw-opacity-0 tw-scale-95 tw-translate-y-2"
            >
                <div 
                    v-if="isOpen"
                    class="tw-absolute tw-top-full tw-left-0 tw-right-0 tw-z-50 tw-mt-1 tw-bg-white tw-border tw-border-gray-200 tw-rounded-lg tw-shadow-lg tw-max-h-60 tw-overflow-y-auto"
                >
                    <!-- Search input -->
                    <div v-if="props.searchable" class="tw-p-3 tw-border-b tw-border-gray-100">
                        <input
                            v-model="searchQuery"
                            type="text"
                            :placeholder="$t('search', 'Search...')"
                            class="tw-w-full tw-px-3 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-md focus:tw-outline-none focus:tw-border-primary-500"
                        />
                    </div>

                    <!-- Options list -->
                    <div class="tw-py-1">
                        <div
                            v-for="option in filteredOptions"
                            :key="option[props.optionValue]"
                            @click="toggleOption(option)"
                            :class="[
                                'tw-px-3 tw-py-2 tw-cursor-pointer tw-text-sm tw-duration-150 tw-flex tw-items-center tw-gap-3',
                                isSelected(option) 
                                    ? 'tw-bg-primary-50 tw-text-primary-700' 
                                    : 'hover:tw-bg-gray-50 tw-text-gray-700'
                            ]"
                        >
                            <div class="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center">
                                <icon 
                                    v-if="isSelected(option)"
                                    icon="solar:check-circle-bold" 
                                    class="tw-text-primary-600 tw-text-sm"
                                />
                                <div 
                                    v-else
                                    class="tw-w-3 tw-h-3 tw-border tw-border-gray-300 tw-rounded"
                                ></div>
                            </div>
                            <div class="tw-flex-1 tw-min-w-0">
                                <div class="tw-font-medium">{{ option[props.optionLabel] }}</div>
                                <div v-if="option.description" class="tw-text-xs tw-text-gray-500 tw-mt-1 tw-leading-relaxed">
                                    {{ option.description }}
                                </div>
                            </div>
                        </div>
                        
                        <div v-if="filteredOptions.length === 0" class="tw-px-3 tw-py-4 tw-text-center tw-text-gray-500 tw-text-sm">
                            {{ searchQuery ? $t('common.no-results-found', 'No results found') : $t('common.no-options-available', 'No options available') }}
                        </div>
                    </div>
                </div>
            </transition>
        </div>

        <!-- Selected items as tags -->
        <div v-if="selectedItems.length > 0" class="tw-mt-3 tw-flex tw-flex-wrap tw-gap-2">
            <div
                v-for="item in selectedItems"
                :key="item.id"
                class="tw-inline-flex tw-items-center tw-gap-2 tw-px-3 tw-py-1 tw-bg-primary-100 tw-text-primary-700 tw-text-sm tw-rounded-full tw-border tw-border-primary-200"
            >
                <span class="tw-font-medium">{{ item.name }}</span>
                <button
                    @click="removeItem(item)"
                    type="button"
                    class="tw-w-4 tw-h-4 tw-flex tw-items-center tw-justify-center tw-rounded-full hover:tw-bg-primary-200 tw-duration-150"
                >
                    <icon icon="solar:close-circle-bold" class="tw-text-xs" />
                </button>
            </div>
        </div>

        <FormErrorMessage v-if="!props.hideErrors">{{ props.error }}</FormErrorMessage>
        <FormHelperMessage 
            v-if="!!props.help"
            :id="ariaDescribedBy"
        >{{ props.help }}</FormHelperMessage>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, defineEmits, defineProps } from 'vue';
import { useI18n } from 'vue-i18n';
import { v4 as uuid } from 'uuid';
import FormLabel from './FormLabel.vue';
import FormErrorMessage from './FormErrorMessage.vue';
import FormHelperMessage from './FormHelperMessage.vue';

const { t } = useI18n();
t
const emit = defineEmits(['update:modelValue']);
const props = defineProps({
    id: {
        type: String,
        default: () => `multi-select-${uuid()}`
    },
    modelValue: {
        type: Array,
        default: () => []
    },
    options: {
        type: Array,
        default: () => []
    },
    label: String,
    placeholder: {
        type: String,
        default: 'Select items'
    },
    required: Boolean,
    error: String,
    help: String,
    leftIcon: String,
    searchable: {
        type: Boolean,
        default: true
    },
    hideLabel: {
        type: Boolean,
        default: false
    },
    hideErrors: {
        type: Boolean,
        default: false
    },
    optionLabel: {
        type: String,
        default: 'name'
    },
    optionValue: {
        type: String,
        default: 'id'
    }
});

const isOpen = ref(false);
const searchQuery = ref('');

const ariaDescribedBy = computed(() => {
    return props.help ? `help-${uuid()}` : null;
});

const selectedItems = computed({
    get: () => props.modelValue || [],
    set: (value) => emit('update:modelValue', value)
});

const filteredOptions = computed(() => {
    if (!searchQuery.value) {
        return props.options;
    }
    
    const query = searchQuery.value.toLowerCase();
    return props.options.filter(option => 
        option.name.toLowerCase().includes(query)
    );
});

const isSelected = (option) => {
    return selectedItems.value.some(item => item[props.optionValue] === option[props.optionValue]);
};

const toggleDropdown = () => {
    isOpen.value = !isOpen.value;
    if (isOpen.value) {
        searchQuery.value = '';
    }
};

const toggleOption = (option) => {
    const isCurrentlySelected = isSelected(option);
    
    if (isCurrentlySelected) {
        selectedItems.value = selectedItems.value.filter(item => item[props.optionValue] !== option[props.optionValue]);
    } else {
        selectedItems.value = [...selectedItems.value, option];
    }
};

const removeItem = (item) => {
    selectedItems.value = selectedItems.value.filter(selectedItem => selectedItem[props.optionValue] !== item[props.optionValue]);
};

// Close dropdown when clicking outside
const handleClickOutside = (event) => {
    if (!event.target.closest(`#${props.id}`) && !event.target.closest('.tw-absolute')) {
        isOpen.value = false;
    }
};

onMounted(() => {
    document.addEventListener('click', handleClickOutside);
});

onUnmounted(() => {
    document.removeEventListener('click', handleClickOutside);
});
</script>

<style scoped>
/* Custom scrollbar for dropdown */
::-webkit-scrollbar {
    width: 6px;
}

::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb {
    background: #c1c1c1;
    border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
    background: #a8a8a8;
}
</style> 